import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'ui-skeleton',
  standalone: true,
  imports: [MatIconModule],
  template: `
    <div class="skeleton-loader animate-pulse p-4 space-y-4 w-full" [class.p-0!]="noPadding()">
      @switch (type()) {
        @case ('code') {
          <div class="space-y-2.5">
            <div class="flex items-center space-x-2 pb-2 border-b border-slate-100">
              <div class="h-3 w-3 rounded-full bg-slate-200"></div>
              <div class="h-3 w-3 rounded-full bg-slate-200"></div>
              <div class="h-3 w-3 rounded-full bg-slate-200"></div>
              <div class="h-4 bg-slate-200 rounded w-16 ml-4"></div>
            </div>
            <div class="h-3.5 bg-slate-200 rounded w-1/3"></div>
            <div class="h-3.5 bg-slate-200 rounded w-4/5 pl-4"></div>
            <div class="h-3.5 bg-slate-200 rounded w-2/3 pl-4"></div>
            <div class="h-3.5 bg-slate-200 rounded w-1/2 pl-8"></div>
            <div class="h-3.5 bg-slate-200 rounded w-3/4"></div>
          </div>
        }
        @case ('image') {
          <div class="space-y-4">
            <div class="h-4 bg-slate-200 rounded w-1/4"></div>
            <div class="h-[300px] bg-slate-100 rounded-lg w-full flex items-center justify-center border border-dashed border-slate-200">
              <div class="flex flex-col items-center space-y-2">
                <mat-icon class="text-slate-300 !text-5xl !h-12 !w-12">image</mat-icon>
                <div class="h-3 bg-slate-200 rounded w-24"></div>
              </div>
            </div>
          </div>
        }
        @case ('video') {
          <div class="space-y-4">
            <div class="h-4 bg-slate-200 rounded w-1/3"></div>
            <div class="h-[340px] bg-slate-100 rounded-lg w-full flex items-center justify-center border border-slate-200">
              <div class="h-14 w-14 rounded-full bg-slate-200 flex items-center justify-center">
                <mat-icon class="text-slate-400 !text-3xl !h-8 !w-8">play_arrow</mat-icon>
              </div>
            </div>
          </div>
        }
        @case ('audio') {
          <div class="flex items-center space-x-4 border border-slate-100 rounded-xl p-4 bg-slate-50/50">
            <div class="h-12 w-12 rounded-full bg-slate-200 flex items-center justify-center">
              <mat-icon class="text-slate-400 !text-2xl !h-6 !w-6">volume_up</mat-icon>
            </div>
            <div class="flex-1 space-y-3">
              <div class="h-3 bg-slate-200 rounded w-1/3"></div>
              <div class="h-1.5 bg-slate-200 rounded w-full"></div>
            </div>
          </div>
        }
        @case ('quiz') {
          <div class="space-y-4 p-2">
            <div class="flex items-center space-x-2">
              <mat-icon class="text-slate-300">help_outline</mat-icon>
              <div class="h-4 bg-slate-200 rounded w-2/3"></div>
            </div>
            <div class="space-y-2.5 pl-8">
              <div class="flex items-center space-x-3">
                <div class="h-5 w-5 rounded-full border-2 border-slate-200 bg-slate-50"></div>
                <div class="h-3 bg-slate-200 rounded w-1/2"></div>
              </div>
              <div class="flex items-center space-x-3">
                <div class="h-5 w-5 rounded-full border-2 border-slate-200 bg-slate-50"></div>
                <div class="h-3 bg-slate-200 rounded w-2/5"></div>
              </div>
              <div class="flex items-center space-x-3">
                <div class="h-5 w-5 rounded-full border-2 border-slate-200 bg-slate-50"></div>
                <div class="h-3 bg-slate-200 rounded w-3/5"></div>
              </div>
            </div>
          </div>
        }
        @case ('file') {
          <div class="flex items-center space-x-4 border border-slate-100 rounded-xl p-4 bg-slate-50/50">
            <div class="h-12 w-12 rounded-lg bg-slate-200 flex items-center justify-center">
              <mat-icon class="text-slate-400 !text-2xl !h-6 !w-6">description</mat-icon>
            </div>
            <div class="flex-1 space-y-2.5">
              <div class="h-3.5 bg-slate-200 rounded w-1/2"></div>
              <div class="h-2.5 bg-slate-200 rounded w-1/4"></div>
            </div>
          </div>
        }
        @case ('canvas-transcript') {
          <div class="border border-slate-100 rounded-xl p-4 bg-white space-y-4">
            <!-- Header bar skeleton -->
            <div class="flex items-center justify-between pb-3 border-b border-slate-100">
              <div class="flex items-center space-x-3 flex-1">
                <div class="h-8 w-8 rounded bg-slate-200"></div>
                <div class="h-4 bg-slate-200 rounded w-1/2"></div>
              </div>
              <div class="flex items-center space-x-2">
                <div class="h-5 w-5 rounded bg-slate-200"></div>
                <div class="h-5 w-5 rounded bg-slate-200"></div>
              </div>
            </div>
            <!-- Body content skeleton -->
            <div class="space-y-3 pt-1">
              <div class="h-5 bg-slate-200 rounded w-1/3"></div>
              <div class="h-3 bg-slate-200 rounded w-full"></div>
              <div class="h-3 bg-slate-200 rounded w-5/6"></div>
            </div>
            <!-- Center see-more button skeleton -->
            <div class="flex justify-center pt-2">
              <div class="h-8 w-20 bg-slate-200 rounded-full"></div>
            </div>
          </div>
        }
        @case ('note-list') {
          <div class="note-list-skeleton">
            @for (i of items(); track i) {
              <div class="note-item-skeleton">
                <div class="skeleton-circle"></div>
                <div class="note-item-content flex-1">
                  <div class="skeleton-title"></div>
                  <div class="skeleton-description"></div>
                  <div class="skeleton-tag"></div>
                </div>
              </div>
            }
          </div>
        }
        @case ('courses-list') {
          <div class="courses-list-skeleton">
            @for (i of items(); track i) {
              <div class="course-card-skeleton">
                <div class="card-header-skeleton">
                  <div class="skeleton-icon"></div>
                  <div class="skeleton-title"></div>
                </div>
                <div class="card-body-skeleton">
                  <div class="skeleton-text"></div>
                  <div class="skeleton-text w-5/6"></div>
                </div>
                <div class="card-footer-skeleton">
                  <div class="skeleton-button"></div>
                </div>
              </div>
            }
          </div>
        }
        @case ('knowledge-navigator') {
          <div class="navigator-skeleton">
            <!-- Solo lecture skeletons -->
            @for (i of [1, 2]; track i) {
              <div class="skeleton-item">
                <div class="skeleton-icon"></div>
                <div class="skeleton-line" style="width: 55%;"></div>
              </div>
            }
            <!-- Section/Folder skeletons -->
            @for (j of [1, 2, 3]; track j) {
              <div class="skeleton-section">
                <div class="flex items-center" style="display: flex; gap: 8px;">
                  <div class="skeleton-icon"></div>
                  <div class="skeleton-line" style="width: 45%;"></div>
                </div>
                <div class="pl-6 space-y-2" style="padding-left: 1.5rem; display: flex; flex-direction: column; gap: 8px; margin-top: 8px;">
                  <div class="flex items-center" style="display: flex; gap: 8px; padding-top: 4px;">
                    <div class="skeleton-icon"></div>
                    <div class="skeleton-line" style="width: 60%;"></div>
                  </div>
                  <div class="flex items-center" style="display: flex; gap: 8px; padding-top: 4px;">
                    <div class="skeleton-icon"></div>
                    <div class="skeleton-line" style="width: 50%;"></div>
                  </div>
                </div>
              </div>
            }
          </div>
        }
        @case ('join-links') {
          <div class="bg-white mb-2 overflow-hidden animate-pulse">
            <!-- Card Header skeleton (reordered to match Title + Button on top, Subtitle below) -->
            <div class="px-6 py-4 bg-slate-50/50 flex flex-col gap-1.5">
              <div class="flex justify-between items-center gap-4">
                <div class="h-4 bg-slate-200 rounded w-48"></div>
                <div class="h-9 w-9 bg-slate-200 rounded-full shrink-0"></div>
              </div>
              <div class="h-3 bg-slate-200 rounded w-80 max-w-full"></div>
            </div>
            <!-- Rows skeleton -->
            @for (i of [1, 2]; track i; let last = $last) {
              <div class="relative flex flex-col md:flex-row md:items-center px-6 py-4 gap-4">
                <div class="flex items-center space-x-3 flex-1 min-w-0">
                  <div class="h-10 w-10 rounded-lg bg-slate-200 shrink-0"></div>
                  <div class="space-y-2 flex-1 min-w-0">
                    <div class="flex items-center space-x-2">
                      <div class="h-4 bg-slate-200 rounded w-36"></div>
                      <!-- Mat-chip style skeletons (rounded rectangles) -->
                      <div class="h-6 bg-slate-200 rounded-[10px] w-20"></div>
                      <div class="h-6 bg-slate-200 rounded-[10px] w-24"></div>
                    </div>
                    <div class="h-3 bg-slate-200 rounded w-64 max-w-full"></div>
                  </div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <!-- Match reordered controls: slide-toggle (rounded-full) + 3 action buttons (rounded-full) -->
                  <div class="h-6 w-10 rounded-full bg-slate-200 me-6"></div>
                  <div class="h-8 w-8 rounded-full bg-slate-200"></div>
                  <div class="h-8 w-8 rounded-full bg-slate-200"></div>
                  <div class="h-8 w-8 rounded-full bg-slate-200"></div>
                </div>
                @if (!last) {
                  <div class="absolute bottom-0 left-6 right-6 h-px bg-slate-200"></div>
                }
              </div>
            }
          </div>
        }
        @case ('user-table') {
          <div class="space-y-4 w-full animate-pulse">
            <!-- Table card skeleton -->
            <div class="bg-white overflow-hidden rounded-2xl">
              <!-- Table Header -->
              <div class="grid grid-cols-4 px-6 py-3 bg-slate-50/50 gap-4">
                <div class="h-3.5 bg-slate-200 rounded w-16"></div>
                <div class="h-3.5 bg-slate-200 rounded w-24"></div>
                <div class="h-3.5 bg-slate-200 rounded w-16"></div>
                <div class="h-3.5 bg-slate-200 rounded w-16 justify-self-end"></div>
              </div>
              <!-- Table Rows -->
              @for (i of items(); track i; let last = $last) {
                <div class="relative grid grid-cols-4 items-center px-6 py-4 gap-4">
                  <div class="h-4 bg-slate-200 rounded w-48"></div>
                  <div class="h-4 bg-slate-200 rounded w-32"></div>
                  <div class="h-8 bg-slate-200 rounded-lg w-28"></div>
                  <div class="h-7 w-7 rounded bg-slate-200 justify-self-end"></div>
                  @if (!last) {
                    <div class="absolute bottom-0 left-6 right-6 h-px bg-slate-100"></div>
                  }
                </div>
              }
            </div>
          </div>
        }
        @default {
          <div class="space-y-3">
            <div class="h-4 bg-slate-200 rounded w-2/3"></div>
            <div class="h-3 bg-slate-200 rounded w-full"></div>
            <div class="h-3 bg-slate-200 rounded w-full"></div>
            <div class="h-3 bg-slate-200 rounded w-5/6"></div>
          </div>
        }
      }
    </div>
  `,
  styleUrl: './skeleton.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkeletonComponent {
  type = input<string>('default');
  noPadding = input<boolean>(false);
  count = input<number>(6);
  items = computed(() => Array.from({ length: this.count() }, (_, i) => i));
}
